/* open space: painted sky + field, drawn live in WebGL behind the page.
   The horizon sits just under the floating hoodie (.product). */
(() => {
  const canvas = document.createElement("canvas");
  canvas.className = "ow-sky";
  canvas.setAttribute("aria-hidden", "true");
  document.body.prepend(canvas);

  const gl = canvas.getContext("webgl", { antialias: false, alpha: false, preserveDrawingBuffer: false });
  if (!gl) { document.documentElement.classList.add("no-sky-gl"); return; }
  document.documentElement.classList.add("has-sky");

  const vert = `
    attribute vec2 p;
    void main() { gl_Position = vec4(p, 0.0, 1.0); }
  `;

  const frag = `
    precision mediump float;
    uniform vec2 uRes;
    uniform float uTime;
    uniform float uHorizon;   // 0..1 from bottom
    uniform vec2 uMouse;      // 0..1, y up
    uniform float uMouseOn;
    uniform float uNight;     // 0 day .. 1 night
    uniform float uDusk;      // 0 .. 1 warm sunset / sunrise light

    float hash(vec2 p) { return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
    float noise(vec2 p) {
      vec2 i = floor(p), f = fract(p);
      vec2 u = f * f * (3.0 - 2.0 * f);
      return mix(mix(hash(i), hash(i + vec2(1, 0)), u.x),
                 mix(hash(i + vec2(0, 1)), hash(i + vec2(1, 1)), u.x), u.y);
    }
    float fbm(vec2 p) {
      float v = 0.0, a = 0.5;
      for (int i = 0; i < 5; i++) { v += a * noise(p); p = p * 2.03 + 17.1; a *= 0.5; }
      return v;
    }

    // sharp-ish peaks for mountain ridgelines
    float ridge(float x) {
      float v = 0.0, a = 0.55, f = 1.0;
      for (int i = 0; i < 4; i++) {
        float n = noise(vec2(x * f, 0.37 * float(i)));
        v += a * (1.0 - abs(n * 2.0 - 1.0));
        f *= 2.1; a *= 0.45;
      }
      return v;
    }

    void main() {
      vec2 uv = gl_FragCoord.xy / uRes;
      float asp = uRes.x / uRes.y;
      float t = uTime;

      // gentle rolling horizon, rising slightly to the left like the reference
      float h = uHorizon
        + 0.012 * sin(uv.x * 2.1 + 0.9)
        + 0.005 * sin(uv.x * 5.3 + 2.1)
        - 0.008 * uv.x;

      // ---------- sky ----------
      float sy = clamp((uv.y - h) / max(1.0 - h, 0.001), 0.0, 1.0);
      // day / dusk / night palettes, blended by time of day
      vec3 skyTop = mix(mix(vec3(0.76, 0.88, 0.97), vec3(0.47, 0.52, 0.78), uDusk), vec3(0.010, 0.016, 0.045), uNight);
      vec3 skyMid = mix(mix(vec3(0.88, 0.94, 0.98), vec3(0.93, 0.70, 0.66), uDusk), vec3(0.020, 0.032, 0.085), uNight);
      vec3 skyLow = mix(mix(vec3(0.96, 0.97, 0.98), vec3(1.00, 0.80, 0.60), uDusk), vec3(0.050, 0.068, 0.13), uNight);
      vec3 sky = mix(skyLow, skyMid, smoothstep(0.0, 0.35, sy));
      sky = mix(sky, skyTop, smoothstep(0.45, 1.0, sy));
      // long soft clouds drifting (pink at dusk, faint and dark at night)
      float cl = fbm(vec2(uv.x * asp * 1.2 + t * 0.006, uv.y * 5.0 - t * 0.002));
      cl = smoothstep(0.52, 0.85, cl) * smoothstep(0.05, 0.6, sy) * mix(0.35, 0.22, uNight);
      vec3 cloudCol = mix(mix(vec3(0.98, 0.99, 1.0), vec3(1.0, 0.86, 0.82), uDusk), vec3(0.07, 0.085, 0.14), uNight);
      sky = mix(sky, cloudCol, cl);

      // stars: twinkling points, only at night, thinning toward the horizon
      vec2 sp = vec2(uv.x * asp, uv.y) * 70.0;
      vec2 cell = floor(sp);
      float rnd = hash(cell);
      vec2 pos = vec2(hash(cell + 3.1), hash(cell + 7.7));
      float dist = length(fract(sp) - pos);
      float star = step(0.955, rnd) * smoothstep(0.09, 0.0, dist);
      star *= 0.55 + 0.45 * sin(t * (1.0 + rnd * 3.0) + rnd * 40.0);
      star *= smoothstep(0.08, 0.5, sy) * (1.0 - cl * 2.5);
      sky += vec3(0.85, 0.9, 1.0) * star * uNight;

      // ---------- mountains: a far pale range and a nearer darker one, lit by the time of day ----------
      // keep peak size steady on narrow screens so phones still get real peaks
      float mx = (uv.x - 0.5) * max(asp, 1.1);
      float farTop  = h + 0.03 + 0.13 * pow(ridge(mx * 2.2 + 3.0), 1.6);
      float nearTop = h + 0.012 + 0.07 * pow(ridge(mx * 3.4 + 11.0), 1.5);
      vec3 farCol  = mix(mix(vec3(0.72, 0.81, 0.90), vec3(0.74, 0.58, 0.68), uDusk), vec3(0.035, 0.05, 0.10), uNight);
      vec3 nearCol = mix(mix(vec3(0.58, 0.68, 0.78), vec3(0.55, 0.43, 0.56), uDusk), vec3(0.02, 0.032, 0.07), uNight);
      // atmospheric haze: each range lightens toward its base
      float farBase  = clamp((uv.y - h) / max(farTop - h, 0.001), 0.0, 1.0);
      float nearBase = clamp((uv.y - h) / max(nearTop - h, 0.001), 0.0, 1.0);
      farCol  = mix(skyLow, farCol, 0.55 + 0.45 * farBase);
      nearCol = mix(skyLow, nearCol, 0.45 + 0.55 * nearBase);
      // a little rock texture so they read as painted, not flat
      float rock = fbm(vec2(mx * 9.0, uv.y * 30.0));
      farCol  *= 0.97 + 0.06 * rock;
      nearCol *= 0.95 + 0.1 * rock;
      float mtn = smoothstep(0.35, 0.9, uNight);   // mountains only come out at night
      sky = mix(sky, farCol,  smoothstep(farTop + 0.002, farTop - 0.002, uv.y) * mtn);
      sky = mix(sky, nearCol, smoothstep(nearTop + 0.002, nearTop - 0.002, uv.y) * mtn);

      // ---------- field ----------
      float d = clamp((h - uv.y) / max(h, 0.001), 0.0, 1.0);   // 0 at horizon, 1 at bottom
      float pd = pow(d, 0.55);
      vec3 haze = vec3(0.47, 0.57, 0.36);
      vec3 mid  = vec3(0.31, 0.44, 0.21);
      vec3 deep = vec3(0.21, 0.33, 0.14);
      vec3 field = mix(haze, mid, smoothstep(0.0, 0.35, pd));
      field = mix(field, deep, smoothstep(0.35, 1.0, pd));
      field *= mix(vec3(1.0), vec3(1.12, 0.9, 0.72), uDusk);
      field *= mix(vec3(1.0), vec3(0.13, 0.18, 0.25), uNight);

      // perspective-ish coords: finer detail near the horizon
      float z = 1.0 / (d + 0.06);
      vec2 g = vec2((uv.x - 0.5) * asp * z * 2.2, z * 1.6);

      // painterly grass texture: horizontal strokes
      float strokes = fbm(vec2(g.x * 1.4, g.y * 6.0));
      field *= 0.88 + 0.22 * strokes;

      // wind: bright bands rolling across the field
      float gust = 0.55 + 0.45 * sin(t * 0.69);
      float wave = sin(g.x * 0.9 - g.y * 0.6 - t * 1.1 + fbm(g * 0.35 + t * 0.05) * 4.0);
      float sheen = smoothstep(0.25, 1.0, wave) * gust;
      vec3 sheenCol = mix(vec3(0.07, 0.08, 0.03), vec3(0.05, 0.07, 0.11), uNight);   // moonlit ripples at night
      field = mix(field, field + sheenCol, sheen * (0.35 + 0.4 * (1.0 - pd)));

      // slow cloud shadows passing over the grass
      float sh = fbm(vec2(uv.x * asp * 0.9 - t * 0.012, d * 2.2));
      field *= 1.0 - smoothstep(0.5, 0.75, sh) * mix(0.22, 0.08, uNight);

      // cursor: grass catches a little light where you point
      vec2 m = vec2((uv.x - uMouse.x) * asp, uv.y - uMouse.y);
      float md = length(m);
      field += mix(vec3(0.07, 0.08, 0.03), vec3(0.06, 0.08, 0.12), uNight) * uMouseOn * smoothstep(0.22, 0.0, md) * step(uv.y, h);

      // ---------- blend ----------
      float edge = smoothstep(h - 0.002, h + 0.004, uv.y);
      vec3 col = mix(field, sky, edge);
      // soft haze line on the horizon
      col = mix(col, skyLow, smoothstep(0.03, 0.0, abs(uv.y - h)) * 0.35);
      // paper grain
      col += (hash(gl_FragCoord.xy + fract(t) * 91.0) - 0.5) * mix(0.035, 0.025, uNight);
      gl_FragColor = vec4(col, 1.0);
    }
  `;

  const sh = (type, src) => {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
    return s;
  };
  const prog = gl.createProgram();
  try {
    gl.attachShader(prog, sh(gl.VERTEX_SHADER, vert));
    gl.attachShader(prog, sh(gl.FRAGMENT_SHADER, frag));
    gl.linkProgram(prog);
  } catch (e) {
    document.documentElement.classList.replace("has-sky", "no-sky-gl");
    canvas.remove();
    return;
  }
  gl.useProgram(prog);
  const buf = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, buf);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const loc = gl.getAttribLocation(prog, "p");
  gl.enableVertexAttribArray(loc);
  gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

  const U = (n) => gl.getUniformLocation(prog, n);
  const uRes = U("uRes"), uTime = U("uTime"), uHorizon = U("uHorizon"), uMouse = U("uMouse"), uMouseOn = U("uMouseOn");
  const uNight = U("uNight"), uDusk = U("uDusk");

  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const SCALE = 0.55; // render soft and cheap; CSS scales it up
  let horizon = 0.4;
  const mouse = { x: 0.5, y: 0.2, on: 0, target: 0 };

  function layout() {
    const w = innerWidth, h = innerHeight;
    canvas.width = Math.max(1, Math.round(w * SCALE));
    canvas.height = Math.max(1, Math.round(h * SCALE));
    gl.viewport(0, 0, canvas.width, canvas.height);
    // mostly sky: the field only takes the bottom of the screen
    horizon = w < 700 ? 0.08 : 0.1;
    if (reduced) draw(performance.now());
  }

  addEventListener("pointermove", (e) => {
    mouse.x = e.clientX / innerWidth;
    mouse.y = 1 - e.clientY / innerHeight;
    mouse.target = e.pointerType === "mouse" ? 1 : 0.8;
  });
  document.addEventListener("pointerleave", () => { mouse.target = 0; });

  // time of day from the visitor's own clock (preview with ?time=day|dusk|night|dawn or ?hour=21.5)
  const tod = window.__owTimeOfDay || { night: 0, dusk: 0, refresh: () => ({ night: 0, dusk: 0 }) };
  const mood = { night: tod.night, dusk: tod.dusk };
  let ease = 0.01;
  setInterval(() => {
    const next = (window.__owTimeOfDay || tod).refresh(true);
    mood.targetNight = next.night; mood.targetDusk = next.dusk; ease = 0.01;
  }, 30000);
  addEventListener("ow:mood", (e) => {
    mood.targetNight = e.detail.night; mood.targetDusk = e.detail.dusk; ease = 0.035;
  });

  const t0 = performance.now();
  function draw(now) {
    if (mood.targetNight != null) {
      mood.night += (mood.targetNight - mood.night) * ease;
      mood.dusk += (mood.targetDusk - mood.dusk) * ease;
      document.documentElement.style.setProperty("--night", mood.night.toFixed(3));
      document.documentElement.classList.toggle("night", mood.night > 0.55);
    }
    gl.uniform1f(uNight, mood.night);
    gl.uniform1f(uDusk, mood.dusk);
    mouse.on += (mouse.target - mouse.on) * 0.06;
    gl.uniform2f(uRes, canvas.width, canvas.height);
    gl.uniform1f(uTime, reduced ? 12 : (now - t0) / 1000);
    gl.uniform1f(uHorizon, horizon);
    gl.uniform2f(uMouse, mouse.x, mouse.y);
    gl.uniform1f(uMouseOn, mouse.on);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    reveal();
  }

  let shown = false;
  const reveal = () => { if (!shown) { shown = true; requestAnimationFrame(() => canvas.classList.add("ready")); } };

  let visible = true;
  document.addEventListener("visibilitychange", () => { visible = !document.hidden; if (visible && !reduced) requestAnimationFrame(loop); });
  function loop(now) {
    if (!visible) return;
    draw(now);
    requestAnimationFrame(loop);
  }

  addEventListener("resize", layout);
  // re-measure once the hoodie has its size
  addEventListener("load", layout);
  layout();
  if (!reduced) requestAnimationFrame(loop);
})();
